import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, ShoppingBagIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { getAdminOrders } from '../../api/adminOrders';
import OrderDetailsPanel from './OrderDetailsPanel';
import {
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  PaymentBadge,
  Pager,
  STATUS_GROUPS,
  SkeletonRow,
  StatusBadge,
  ago,
  fmtMoney,
  inputCls,
} from './ui';

const PAGE_SIZE = 20;
const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Сначала новые' },
  { value: 'updated_desc', label: 'Недавно изменённые' },
  { value: 'created_asc', label: 'Сначала старые' },
  { value: 'total_desc', label: 'Большая сумма' },
];
const EMPTY = { q: '', group: 'all', payment_status: '', payment_method: '', created_from: '', created_to: '', sort: 'created_desc' };

const OrdersTab = ({ notify, onOpenUser }) => {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [selectedId, setSelectedId] = useState(null);
  const detailsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const group = STATUS_GROUPS.find((g) => g.key === applied.group);
      const data = await getAdminOrders({
        page,
        limit: PAGE_SIZE,
        q: applied.q.trim(),
        status: group?.statuses.join(','),
        payment_status: applied.payment_status,
        payment_method: applied.payment_method,
        created_from: applied.created_from,
        created_to: applied.created_to,
        sort: applied.sort,
      });
      setOrders(data.items || []);
      setSummary(data.summary || null);
      setTotal(data.total);
      setTotalPages(data.pages || 1);
    } catch (err) {
      setOrders([]);
      notify('error', `Ошибка загрузки заказов: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [page, applied, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (next) => {
    setForm(next);
    setApplied(next);
    setPage(1);
  };

  const groupCount = (g) => {
    if (!summary?.by_status) return null;
    const counts = summary.by_status;
    return g.statuses.length ? g.statuses.reduce((s, st) => s + (counts[st] || 0), 0) : Object.values(counts).reduce((a, b) => a + b, 0);
  };

  const openOrder = (id) => {
    setSelectedId(id);
    if (window.innerWidth < 1024) setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleUpdated = useCallback((updated) => {
    setOrders((list) => list.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
  }, []);

  const hasFilters = JSON.stringify(applied) !== JSON.stringify(EMPTY);

  return (
    <div className="space-y-4">
      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { label: 'Найдено заказов', value: typeof total === 'number' ? total.toLocaleString('ru-RU') : '—' },
          { label: 'Новых — ждут продавца', value: (summary?.by_status?.supplier_query || 0).toLocaleString('ru-RU') },
          { label: 'Оплачено (в выборке)', value: (summary?.paid_count || 0).toLocaleString('ru-RU') },
          { label: 'Сумма оплаченных', value: fmtMoney(summary?.paid_amount || 0) },
        ].map((k) => (
          <div key={k.label} className="rounded-block border border-border-primary bg-bg-card p-4 shadow-sm">
            <div className="text-xs text-text-secondary">{k.label}</div>
            <div className="mt-1 text-xl font-bold text-text-primary">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          apply({ ...form });
        }}
        className="rounded-block border border-border-primary bg-bg-card p-4 shadow-sm"
      >
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="№ заказа, имя, телефон или email покупателя / продавца…"
              aria-label="Поиск заказов"
              value={form.q}
              onChange={(e) => setForm((f) => ({ ...f, q: e.target.value }))}
              className={`${inputCls} pl-9`}
            />
          </div>
          <select aria-label="Оплата" value={form.payment_status} onChange={(e) => apply({ ...form, payment_status: e.target.value })} className={`${inputCls} md:w-40`}>
            <option value="">Любая оплата</option>
            {PAYMENT_STATUSES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <select aria-label="Способ оплаты" value={form.payment_method} onChange={(e) => apply({ ...form, payment_method: e.target.value })} className={`${inputCls} md:w-44`}>
            <option value="">Любой способ</option>
            {PAYMENT_METHODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <input type="date" aria-label="Создан с" value={form.created_from} max={form.created_to || undefined} onChange={(e) => setForm((f) => ({ ...f, created_from: e.target.value }))} className={`${inputCls} md:w-40`} />
          <input type="date" aria-label="Создан по" value={form.created_to} min={form.created_from || undefined} onChange={(e) => setForm((f) => ({ ...f, created_to: e.target.value }))} className={`${inputCls} md:w-40`} />
          <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
            <MagnifyingGlassIcon className="h-4 w-4" />
            Искать
          </button>
        </div>
        {hasFilters && (
          <button type="button" onClick={() => apply(EMPTY)} className="mt-2 text-xs font-medium text-text-secondary hover:text-text-primary hover:underline">
            Сбросить фильтры
          </button>
        )}
      </form>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-5">
        <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm lg:col-span-3">
          {/* Status tabs */}
          <div className="flex items-center gap-1 overflow-x-auto border-b border-border-primary px-2 py-2">
            {STATUS_GROUPS.map((g) => {
              const active = applied.group === g.key;
              const count = groupCount(g);
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => apply({ ...applied, group: g.key })}
                  className={`shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium ${
                    active ? 'bg-[#4F45E5]/10 text-[#7C3AED] dark:text-indigo-300' : 'text-text-secondary hover:bg-bg-tertiary'
                  }`}
                >
                  {g.label}
                  {count !== null && <span className="ml-1 text-xs text-text-muted">{count}</span>}
                </button>
              );
            })}
            <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">
              <select
                value={applied.sort}
                onChange={(e) => apply({ ...applied, sort: e.target.value })}
                aria-label="Сортировка"
                className="rounded-lg border border-border-primary bg-bg-primary px-2 py-1 text-xs font-medium text-text-primary focus:outline-none"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button type="button" onClick={load} disabled={loading} title="Обновить" className="rounded-lg p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:opacity-50">
                <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <ul className="divide-y divide-border-primary">
            {loading && Array.from({ length: orders.length || 6 }).map((_, i) => <SkeletonRow key={i} />)}
            {!loading && orders.length === 0 && (
              <li className="px-6 py-16 text-center">
                <ShoppingBagIcon className="mx-auto mb-3 h-12 w-12 text-text-muted" />
                <h3 className="mb-1 text-base font-semibold text-text-primary">Заказов не найдено</h3>
                <p className="text-sm text-text-secondary">Попробуйте изменить фильтры.</p>
              </li>
            )}
            {!loading &&
              orders.map((o) => {
                const selected = selectedId === o.id;
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => openOrder(o.id)}
                      aria-current={selected ? 'true' : undefined}
                      className={`relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${selected ? 'bg-[#4F45E5]/[0.08]' : 'hover:bg-bg-tertiary/60'}`}
                    >
                      {selected && <span className="absolute inset-y-0 left-0 w-1 bg-accent-grad" />}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-text-primary">#{o.id}</span>
                          <span className="truncate text-sm text-text-primary">
                            {o.first_item_title || (o.announcement_id ? `Объявление #${o.announcement_id}` : 'Без позиций')}
                            {o.items_count > 1 && <span className="text-text-muted"> +{o.items_count - 1}</span>}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-text-secondary">
                          <span>Покупатель: {o.buyer?.name || (o.buyer_id ? `#${o.buyer_id}` : '—')}</span>
                          <span>Продавец: {o.supplier?.name || (o.supplier_id ? `#${o.supplier_id}` : '—')}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          <StatusBadge status={o.status} />
                          <PaymentBadge status={o.payment_status} />
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-semibold text-text-primary">{fmtMoney(o.total_amount)}</div>
                        <div className="text-[11px] text-text-muted">{ago(o.created_at)}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
          </ul>
          <Pager page={page} totalPages={totalPages} total={total} shown={orders.length} pageSize={PAGE_SIZE} loading={loading} onPage={setPage} />
        </section>

        <aside ref={detailsRef} className="lg:col-span-2">
          <div className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm lg:sticky lg:top-4 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
            {selectedId ? (
              <OrderDetailsPanel
                key={selectedId}
                orderId={selectedId}
                onClose={() => setSelectedId(null)}
                onUpdated={handleUpdated}
                onOpenUser={onOpenUser}
                notify={notify}
              />
            ) : (
              <div className="px-6 py-16 text-center">
                <ShoppingBagIcon className="mx-auto mb-3 h-10 w-10 text-text-muted" />
                <p className="text-sm text-text-secondary">Выберите заказ, чтобы увидеть детали и управлять им</p>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default OrdersTab;
